import omit from 'lodash/omit'
import trim from 'lodash/trim'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'

import { useInsertAccountMutation } from 'api/accounts/insertAccount'
import { CUSTOMER_DETAILS_TABS_ID } from 'containers/customer-details/settings'
import { getCustomerDetailsPageUrl, isCustomerDetailsPage } from 'router/routes'
import T from 'T'
import { handleErrorWithFetchError } from 'utils/error'

import { INITIAL_EMAIL_CONTACT_METHOD, INITIAL_PHONE_CONTACT_METHOD, reasonRequiresCompetitor } from './model'

import type { NewAccountFormValues } from './model'
import type { NewAccountLifecycle } from './useNewAccountLifecycle'
import type { MutableRefObject } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import type { ContactMethodBody, InsertAccountBody } from 'types/api/account/accountRequests'

const { ACCOUNT_TAB_ID } = CUSTOMER_DETAILS_TABS_ID
const REQUIRED_ADDRESS_FIELDS = ['line1', 'city', 'state'] as const

// contactMethods covers two fields at once, so it stays a toast rather than marking the wrong one.
const SERVER_ERROR_FIELDS: Record<string, keyof NewAccountFormValues> = {
  sourceReasonId: 'sourceReasonId',
  sourceCompetitorId: 'sourceCompetitorId',
  businessUnitId: 'businessUnitId',
}

type NewAccountSubmitArgs = {
  form: UseFormReturn<NewAccountFormValues>
  lifecycle: NewAccountLifecycle
  isOpen: boolean
  onClose: () => void
  // The drawer's own open-focus timer, which would otherwise steal focus back from the email field.
  openFocusTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | undefined>
}

/**
 * The New account submit path: the required-field sweep, the print-only prompt a blank billing email
 * raises, the create itself and where a server refusal lands. Nothing posts until the lifecycle rules load.
 */
const useNewAccountSubmit = ({ form, lifecycle, isOpen, onClose, openFocusTimerRef }: NewAccountSubmitArgs) => {
  const navigate = useNavigate()
  const [insertAccount, { isLoading: isCreating }] = useInsertAccountMutation()
  const [printOnlyValues, setPrintOnlyValues] = useState<NewAccountFormValues | null>(null)
  const emailFocusTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  // Enter submits from every field, and the mutation's isLoading only lands on the next render.
  const isSubmittingRef = useRef(false)
  const { handleSubmit, getValues, setFocus, setError, clearErrors } = form

  const isFieldRendered = useCallback(
    (field: keyof NewAccountFormValues) => {
      if (field === 'businessUnitId') return lifecycle.requireBusinessUnit
      // The competitor select only mounts under a reason that asks for one.
      if (field === 'sourceCompetitorId') {
        return lifecycle.isEnabled && reasonRequiresCompetitor(lifecycle.reasons, getValues('sourceReasonId'))
      }
      return lifecycle.isEnabled
    },
    [lifecycle.requireBusinessUnit, lifecycle.isEnabled, lifecycle.reasons, getValues]
  )

  const buildPayload = useCallback(
    (data: NewAccountFormValues, receivesPrintedCopy: boolean): InsertAccountBody => {
      const { accountName, address, zones, useAsBillingAddress, billingAddress, contact, contactPhone, contactEmail, billingProfileId } =
        data
      const resolvedBillingAddress = useAsBillingAddress ? address : billingAddress

      const payload: InsertAccountBody = {
        billedForOtherCustomer: false,
        accountName: trim(accountName),
        billingAddress: omit(resolvedBillingAddress, ['addressName']),
        location: { address, tags: [], zones },
        tags: [],
        accountBilling: { billingProfileId, receivesPrintedCopy },
        ...(data.businessUnitId && { businessUnitId: data.businessUnitId }),
        ...(trim(data.note) && { note: trim(data.note) }),
        ...(lifecycle.isEnabled && {
          ...(data.sourceReasonId && { sourceReasonId: data.sourceReasonId }),
          ...(data.sourceCompetitorId && { sourceCompetitorId: data.sourceCompetitorId }),
        }),
      }

      if (contact?.firstName || contact?.lastName || contactPhone || contactEmail) {
        const contactMethods: ContactMethodBody[] = []
        if (contactPhone) {
          contactMethods.push({ ...INITIAL_PHONE_CONTACT_METHOD, methodValue: contactPhone })
        }
        if (contactEmail) {
          contactMethods.push({ ...INITIAL_EMAIL_CONTACT_METHOD, methodValue: contactEmail })
        }
        payload.contacts = [{ ...contact, associatedWithAccount: true, associatedWithLocation: true, contactMethods }]
      }

      return payload
    },
    [lifecycle.isEnabled]
  )

  const createAccount = useCallback(
    async (data: NewAccountFormValues, receivesPrintedCopy: boolean) => {
      if (isSubmittingRef.current) return
      isSubmittingRef.current = true
      try {
        const { id: accountId } = await insertAccount(buildPayload(data, receivesPrintedCopy)).unwrap()
        // Leave the drawer open rather than route to an id the server did not send.
        if (!accountId) return
        onClose()
        const newLink = `${getCustomerDetailsPageUrl(accountId)}?tab=${ACCOUNT_TAB_ID}`
        if (isCustomerDetailsPage()) {
          window.location.href = newLink
          return
        }
        navigate(newLink)
      } catch (error) {
        const exceptionType = (error as { data?: { exceptionType?: string } })?.data?.exceptionType
        const field = exceptionType ? SERVER_ERROR_FIELDS[exceptionType] : undefined
        // Marking a control the tenant's settings never rendered would swallow the rejection entirely.
        if (!field || !isFieldRendered(field)) {
          handleErrorWithFetchError(error)
          return
        }
        setError(field, { type: 'server', message: (error as { data?: { message?: string } })?.data?.message })
      } finally {
        isSubmittingRef.current = false
      }
    },
    [insertAccount, buildPayload, onClose, navigate, setError, isFieldRendered]
  )

  // Controlled dropdowns and setValue-written addresses never register, so no rule of theirs can fire.
  const flagBlankUnregisteredFields = useCallback(
    (data: NewAccountFormValues) => {
      const blanks: Array<keyof NewAccountFormValues | `billingAddress.${string}` | `address.${string}`> = []
      clearErrors(['billingProfileId', 'businessUnitId'])
      clearErrors(REQUIRED_ADDRESS_FIELDS.flatMap(field => [`address.${field}` as const, `billingAddress.${field}` as const]))

      REQUIRED_ADDRESS_FIELDS.filter(field => !data.address[field]?.trim()).forEach(field => blanks.push(`address.${field}`))
      if (!data.billingProfileId) blanks.push('billingProfileId')
      if (lifecycle.requireBusinessUnit && !data.businessUnitId) blanks.push('businessUnitId')
      if (!data.useAsBillingAddress) {
        REQUIRED_ADDRESS_FIELDS.filter(field => !data.billingAddress[field]?.trim()).forEach(field =>
          blanks.push(`billingAddress.${field}`)
        )
      }

      blanks.forEach(field => setError(field as 'accountName', { type: 'required', message: T.CANNOT_BE_EMPTY }))
      return blanks.length > 0
    },
    [clearErrors, setError, lifecycle.requireBusinessUnit]
  )

  const onSubmitHandler = useCallback(
    (data: NewAccountFormValues) => {
      if (isSubmittingRef.current || !lifecycle.isReady) return
      if (flagBlankUnregisteredFields(data)) return

      // Only a tenant that requires the email is offered a printed copy for a blank one; elsewhere it just saves.
      if (lifecycle.requireBillingEmail && !data.contactEmail?.trim()) {
        setPrintOnlyValues(data)
        return
      }
      createAccount(data, false)
    },
    [lifecycle.isReady, lifecycle.requireBillingEmail, flagBlankUnregisteredFields, createAccount]
  )

  // The invalid branch runs the same sweep, so a rule failure elsewhere still reds these fields.
  const submitForm = useMemo(
    () => handleSubmit(onSubmitHandler, () => flagBlankUnregisteredFields(getValues())),
    [handleSubmit, onSubmitHandler, flagBlankUnregisteredFields, getValues]
  )

  const handleGoBackAndAddEmail = useCallback(() => {
    setPrintOnlyValues(null)
    // The confirm's focus trap and the drawer's own open-focus both fight for this; drop theirs.
    clearTimeout(openFocusTimerRef.current)
    emailFocusTimerRef.current = setTimeout(() => setFocus('contactEmail'), 0)
  }, [openFocusTimerRef, setFocus])

  const handleSetToPrintOnly = useCallback(() => {
    if (!printOnlyValues) return
    setPrintOnlyValues(null)
    createAccount(printOnlyValues, true)
  }, [printOnlyValues, createAccount])

  useEffect(() => {
    if (isOpen) setPrintOnlyValues(null)
  }, [isOpen])

  useEffect(() => () => clearTimeout(emailFocusTimerRef.current), [])

  return {
    submitForm,
    isCreating,
    isPrintOnlyPromptOpen: Boolean(printOnlyValues),
    handleGoBackAndAddEmail,
    handleSetToPrintOnly,
  }
}

export default useNewAccountSubmit
